import React from 'react';
import Button from '../common/Button';

interface ChallengeCompletedPopupProps {
  score: number;
  requiredCorrectAnswers: number;
  currentRound: number;
  totalRounds: number;
  onContinue: () => void;
  onViewResults: () => void;
}

const ChallengeCompletedPopup: React.FC<ChallengeCompletedPopupProps> = ({
  score,
  requiredCorrectAnswers,
  currentRound,
  totalRounds,
  onContinue,
  onViewResults,
}) => {
  const remainingRounds = totalRounds - currentRound;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-dark-800 to-dark-900 border border-primary-500 rounded-xl p-6 sm:p-8 max-w-md w-full mx-4 relative overflow-hidden shadow-2xl">
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-circuit opacity-5"></div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-accent-500/10 rounded-full blur-2xl animate-pulse-slow"></div>
        
        {/* Corner accents */}
        <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-accent-400 opacity-60"></div>
        <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-accent-400 opacity-60"></div>
        <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-accent-400 opacity-60"></div>
        <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-accent-400 opacity-60"></div>
        
        {/* Scanning line effect */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-accent-400 to-transparent animate-scan opacity-30"></div>
        
        <div className="relative z-10">
          {/* Success indicator */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent-600 to-accent-700 rounded-full mb-4 shadow-[0_0_20px_rgba(34,197,94,0.4)]">
              <div className="text-2xl">🎉</div>
            </div>
            
            <h2 className="text-xl sm:text-2xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-primary-400 tracking-wider uppercase mb-2">
              Challenge Conquered!
            </h2>
            
            <div className="inline-flex items-center px-4 py-2 bg-accent-500/20 border border-accent-500/30 rounded-lg">
              <div className="w-2 h-2 bg-accent-400 rounded-full animate-pulse mr-2"></div>
              <span className="text-accent-300 font-mono text-sm tracking-wider uppercase">
                Target Achieved
              </span>
            </div>
          </div>
          
          {/* Score display */}
          <div className="text-center mb-6">
            <div className="bg-gradient-to-br from-dark-700 to-dark-800 border border-dark-600 rounded-lg p-4 mb-4">
              <div className="text-2xl font-display font-bold text-accent-400 mb-1">
                {score} / {requiredCorrectAnswers}
              </div>
              <p className="text-dark-300 font-mono text-xs tracking-wider uppercase">
                Neural Networks Identified
              </p>
            </div>
            
            <p className="text-primary-300 font-mono text-sm leading-relaxed">
              Outstanding work, Agent! You've successfully identified the required AI patterns.
              {remainingRounds > 0 && (
                <span className="block mt-2 text-dark-400">
                  {remainingRounds} challenge{remainingRounds !== 1 ? 's' : ''} remaining in this mission.
                </span>
              )}
            </p>
          </div>
          
          {/* Action buttons */}
          <div className="space-y-3">
            {remainingRounds > 0 && (
              <Button
                onClick={onContinue}
                variant="success"
                size="large"
                fullWidth
                className="group"
              >
                <div className="flex items-center justify-center space-x-3">
                  <span className="font-display tracking-wider">Continue Mission</span>
                  <div className="w-0 h-0 border-l-4 border-l-white border-y-2 border-y-transparent group-hover:translate-x-1 transition-transform"></div>
                </div>
              </Button>
            )}
            
            <Button
              onClick={onViewResults}
              variant={remainingRounds > 0 ? "secondary" : "success"}
              size="large"
              fullWidth
              className="group"
            >
              <div className="flex items-center justify-center space-x-3">
                <span className="font-display tracking-wider">
                  {remainingRounds > 0 ? "View Results" : "Mission Complete"}
                </span>
                <div className="w-0 h-0 border-l-4 border-l-white border-y-2 border-y-transparent group-hover:translate-x-1 transition-transform"></div>
              </div>
            </Button>
          </div>
          
          {/* Additional info */}
          <div className="mt-4 text-center">
            <div className="inline-flex items-center space-x-2 text-dark-400 font-mono text-xs">
              <div className="w-1 h-1 bg-primary-500 rounded-full animate-pulse"></div>
              <span>Mission Progress: Round {currentRound}/{totalRounds}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeCompletedPopup;